/**
 * Utility functions for order form operations
 */

/**
 * Extract video ID from YouTube URL
 * @param {string} url - YouTube URL
 * @returns {string|null} - Video ID or null if not found
 */
export const extractVideoId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  return match ? match[1].substring(0, 11) : null;
};

/**
 * Calculate order price based on quantity and service price
 * @param {number} quantity - Order quantity
 * @param {number} servicePrice - Price per 1000 units
 * @returns {string} - Formatted price string
 */
export const calculatePrice = (quantity, servicePrice) => {
  if (!quantity || !servicePrice) return "0.00";
  return ((quantity * (servicePrice / 1000)).toFixed(2));
};

/**
 * Validate minimum quantity requirement
 * @param {number} quantity - Order quantity
 * @returns {boolean} - True if quantity is valid
 */
export const isValidQuantity = (quantity) => {
  return quantity >= 100;
};

/**
 * Get YouTube API URL for video data
 * @param {string} videoId - YouTube video ID
 * @param {string} apiKey - YouTube API key
 * @returns {string} - API URL
 */
export const getYouTubeApiUrl = (videoId, apiKey) => {
  return `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${apiKey}&part=snippet,contentDetails`;
};

/**
 * Create order data object for WooCommerce
 * @param {Object} params - Order parameters
 * @returns {Object} - Order data object
 */
export const createOrderData = ({
  customer_id,
  service,
  product_id,
  quantity,
  price,
  youtube_url,
  service_type,
  serviceId,
  order_id = 0
}) => {
  return {
    customer_id,
    service,
    product_id,
    quantity,
    meta_data: [
      { key: "price", value: price },
      { key: "youtube_url", value: youtube_url },
      { key: "service", value: service },
      { key: "service_type", value: service_type },
      { key: "quantity", value: quantity },
      { key: "service_id", value: serviceId },
      { key: "order_id", value: order_id },
    ],
  };
};

/**
 * Create fast order data for external API
 * @param {Object} params - Order parameters
 * @returns {Object} - Fast order data
 */
export const createFastOrderData = ({
  serviceId,
  youtube_url,
  quantity,
  userId,
  price
}) => {
  return {
    action: "add",
    service: serviceId,
    link: youtube_url,
    quantity: quantity,
    userId: userId,
    amount: price,
  };
};
